import React, { useRef, useEffect } from 'react';
import './input-otp.scss';

export const InputOTP = ({ maxLength = 6, value = '', onChange, children, ...props }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        const handleInput = (e) => {
            if (onChange && e.target?.value) {
                onChange(e.target.value);
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('input', handleInput);
            return () => container.removeEventListener('input', handleInput);
        }
    }, [onChange]);

    return (
        <div ref={containerRef} className="input-otp-container" {...props}>
            {children}
        </div>
    );
};

export const InputOTPGroup = ({ className = '', children, ...props }) => {
    return (
        <div className={`input-otp-group ${className}`} {...props}>
            {children}
        </div>
    );
};

export const InputOTPSlot = ({ index, className = '', ...props }) => {
    const inputRef = useRef(null);
    const parentOTP = useRef(null);

    useEffect(() => {
        // Find parent OTP component
        let parent = inputRef.current?.parentElement;
        while (parent && !parent.classList.contains('input-otp-container')) {
            parent = parent.parentElement;
        }
        parentOTP.current = parent;
    }, []);

    const handleChange = (e) => {
        const val = e.target.value;
        if (val.length <= 1 && /^\d*$/.test(val)) {
            // Get all inputs
            const inputs = parentOTP.current?.querySelectorAll('input');
            if (inputs) {
                const newValue = Array.from(inputs).map(input => input.value).join('');

                // Trigger onChange on parent
                const event = new Event('input', { bubbles: true });
                Object.defineProperty(event, 'target', { value: { value: newValue }, enumerable: true });
                parentOTP.current?.dispatchEvent(event);

                // Move to next input if value entered
                if (val && index < inputs.length - 1) {
                    inputs[index + 1]?.focus();
                }
            }
        }
    };

    const handleKeyDown = (e) => {
        const inputs = parentOTP.current?.querySelectorAll('input');
        if (e.key === 'Backspace' && !e.target.value && index > 0 && inputs) {
            inputs[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (/^\d+$/.test(pastedData)) {
            const inputs = parentOTP.current?.querySelectorAll('input');
            if (inputs) {
                pastedData.split('').forEach((char, i) => {
                    if (inputs[i]) {
                        inputs[i].value = char;
                    }
                });
                const newValue = pastedData;
                const event = new Event('input', { bubbles: true });
                Object.defineProperty(event, 'target', { value: { value: newValue }, enumerable: true });
                parentOTP.current?.dispatchEvent(event);

                // Focus last filled input
                const lastIndex = Math.min(pastedData.length, inputs.length) - 1;
                inputs[lastIndex]?.focus();
            }
        }
    };

    // Get value for this slot from parent
    const getSlotValue = () => {
        const inputs = parentOTP.current?.querySelectorAll('input');
        return inputs?.[index]?.value || '';
    };

    return (
        <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            maxLength={1}
            className={`input-otp-slot ${className}`}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            {...props}
        />
    );
};
